// Core packages
import { NgModule } from "@angular/core";
import { RouterModule, Routes } from "@angular/router";

// Third party packages

// Custom packages
import { AdminQRCodeMappingComponent } from "src/app/components/adminQRCodeMapping/adminQRCodeMapping.component";
import { PrivacyDocsPublicAccessComponent } from "src/app/components/privacyDocsPublicAccess/privacyDocsPublicAccess.component";
import { SupplierSecurityVideoComponent } from "src/app/components/supplierSecurityVideo/supplierSecurityVideo.component";
import { AuthGuard } from "src/app/shared/guards/auth.guard";
import { LangGuard } from "src/app/shared/guards/lang.guard";
import { AppWrapperComponent } from "./components/app-wrapper/app-wrapper.component";
import { HomeComponent } from "./components/home/home.component";
import { PublicQRCodeComponent } from "./components/publicQRCode/publicQRCode.component";
import { PublicQrV3LegacyGuard } from "src/app/shared/guards/public-qr-v3-legacy.guard";

// Define routes
const routes: Routes = [
  { path: "supplier-security-video/:supplierId", pathMatch: "prefix", component: SupplierSecurityVideoComponent },
  // Public QR page of the new archive (NEW-A3 FE). FIRST, before the legacy `qrcode/...` routes:
  // their last one takes any five segments, `qrcode/v3/legacy/doc/<a>/<b>` included.
  // Addresses printed from now on: a document, an area, a section, an element or a unit section.
  {
    path: "qrcode/d/:docId",
    pathMatch: "full",
    data: { qrKind: "document" },
    loadChildren: () =>
      import("./modules/public-qr-v3/public-qr-v3.module").then((m) => m.PublicQrV3Module),
  },
  {
    path: "qrcode/a/:archiveId/:areaId",
    pathMatch: "full",
    data: { qrKind: "area" },
    loadChildren: () =>
      import("./modules/public-qr-v3/public-qr-v3.module").then((m) => m.PublicQrV3Module),
  },
  {
    path: "qrcode/s/:archiveId/:contentId",
    pathMatch: "full",
    data: { qrKind: "section" },
    loadChildren: () =>
      import("./modules/public-qr-v3/public-qr-v3.module").then((m) => m.PublicQrV3Module),
  },
  {
    path: "qrcode/s/:archiveId/:contentId/:elementId",
    pathMatch: "full",
    data: { qrKind: "section" },
    loadChildren: () =>
      import("./modules/public-qr-v3/public-qr-v3.module").then((m) => m.PublicQrV3Module),
  },
  // Stickers already printed, read through the new archive: reached from the legacy routes below
  // once `archiveV3Live` is on (PublicQrV3LegacyGuard).
  {
    path: "qrcode/v3/legacy/doc/:documentId/:subdocid",
    pathMatch: "full",
    data: { qrKind: "legacyDoc" },
    loadChildren: () =>
      import("./modules/public-qr-v3/public-qr-v3.module").then((m) => m.PublicQrV3Module),
  },
  {
    path: "qrcode/v3/legacy/section/:documentId/:section",
    pathMatch: "full",
    data: { qrKind: "legacySection" },
    loadChildren: () =>
      import("./modules/public-qr-v3/public-qr-v3.module").then((m) => m.PublicQrV3Module),
  },
  {
    path: "qrcode/v3/legacy/section/:documentId/:section/:subSection",
    pathMatch: "full",
    data: { qrKind: "legacySection" },
    loadChildren: () =>
      import("./modules/public-qr-v3/public-qr-v3.module").then((m) => m.PublicQrV3Module),
  },
  {
    path: "qrcode/v3/legacy/section/:documentId/:section/:subSection/:structureId",
    pathMatch: "full",
    data: { qrKind: "legacySection" },
    loadChildren: () =>
      import("./modules/public-qr-v3/public-qr-v3.module").then((m) => m.PublicQrV3Module),
  },
  {
    path: "qrcode/v3/legacy/sectionold/:propertyId/:sectionold",
    pathMatch: "full",
    data: { qrKind: "legacySectionold" },
    loadChildren: () =>
      import("./modules/public-qr-v3/public-qr-v3.module").then((m) => m.PublicQrV3Module),
  },
  { path: "qrcode/:documentId/fireSafetyDoc", pathMatch: "prefix", component: PublicQRCodeComponent },
  { path: "qrcode/:documentId/genericDoc", pathMatch: "prefix", component: PublicQRCodeComponent },
  { path: "qrcode/:documentId/privacyStudioDoc", pathMatch: "prefix", component: PublicQRCodeComponent },
  { path: "qrcode/:propertyId/videoext", pathMatch: "prefix", component: PublicQRCodeComponent },
  { path: "qrcode/:propertyId/video", pathMatch: "prefix", component: PublicQRCodeComponent },
  { path: "qrcode/:companyId/safetyVideoSuppliers", pathMatch: "prefix", component: PublicQRCodeComponent },
  { path: "qrcode/:companyId/safetyVideoResidents", pathMatch: "prefix", component: PublicQRCodeComponent },
  { path: "qrcode/:propertyId/video/:token", pathMatch: "prefix", component: PublicQRCodeComponent },
  { path: "qrcode/:propertyId/docstructureext/:sectionold", pathMatch: "full", component: PublicQRCodeComponent, canActivate: [PublicQrV3LegacyGuard], data: { publicQrLegacy: "sectionold" } },
  { path: "qrcode/:documentId/docstructure/:section", pathMatch: "full", component: PublicQRCodeComponent, canActivate: [PublicQrV3LegacyGuard], data: { publicQrLegacy: "section" } },
  { path: "qrcode/:documentId/docsubstructure/:section/:subSection/:structureId", pathMatch: "full", component: PublicQRCodeComponent, canActivate: [PublicQrV3LegacyGuard], data: { publicQrLegacy: "section" } },
  { path: "qrcode/:documentId/docsubstructure/:section/:subSection", pathMatch: "full", component: PublicQRCodeComponent, canActivate: [PublicQrV3LegacyGuard], data: { publicQrLegacy: "section" } },
  { path: "qrcode/:documentId/:doc/:type/:key/:subdocid", pathMatch: "full", component: PublicQRCodeComponent, canActivate: [PublicQrV3LegacyGuard], data: { publicQrLegacy: "doc" } },
  { path: "qrcode-admin-mapping/:structureId", pathMatch: "full", component: AdminQRCodeMappingComponent },
  {
    path: "privacy-docs/:companyId/condo-owner",
    pathMatch: "full",
    component: PrivacyDocsPublicAccessComponent,
    data: { docType: "condo-owner" },
  },
  {
    path: "privacy-docs/:companyId/supplier",
    pathMatch: "full",
    component: PrivacyDocsPublicAccessComponent,
    data: { docType: "supplier" },
  },
  // Same as above, plus an optional trailing ":supplierId" segment (Angular
  // has no native syntax for an optional path param, so it's a second route
  // entry instead): when present, it scopes the "master" check/download
  // calls to that specific supplier (@see PrivacyDocsPublicAccessComponent)
  {
    path: "privacy-docs/:companyId/supplier/:supplierId",
    pathMatch: "full",
    component: PrivacyDocsPublicAccessComponent,
    data: { docType: "supplier" },
  },
  {
    path: ":lang",
    canActivate: [LangGuard],
    children: [
      {
        path: "auth",
        loadChildren: () =>
          import("./modules/auth/auth.module").then((m) => m.AuthModule),
      },
      {
        path: "app",
        component: AppWrapperComponent,
        canActivate: [AuthGuard],
        children: [
          {
            path: "",
            pathMatch: "full",
            component: HomeComponent,
          },
          {
            path: "users",
            loadChildren: () =>
              import("./modules/users/users.module").then((m) => m.UsersModule),
          },
          {
            path: "temporaries",
            loadChildren: () =>
              import("./modules/temporaries/temporaries.module").then(
                (m) => m.TemporariesModule
              ),
          },
          {
            path: "permissions",
            loadChildren: () =>
              import("./modules/permissions/permissions.module").then(
                (m) => m.PermissionsModule
              ),
          },
          {
            path: "roles",
            loadChildren: () =>
              import("./modules/roles/roles.module").then((m) => m.RolesModule),
          },
          {
            path: "companies",
            loadChildren: () =>
              import("./modules/companies/companies.module").then(
                (m) => m.CompaniesModule
              ),
          },
          {
            path: "suppliers",
            loadChildren: () =>
              import("./modules/suppliers/suppliers.module").then(
                (m) => m.SuppliersModule
              ),
          },
          {
            path: "properties",
            loadChildren: () =>
              import("./modules/properties/properties.module").then(
                (m) => m.PropertiesModule
              ),
          },
          {
            path: "services",
            loadChildren: () =>
              import("./modules/services/services.module").then(
                (m) => m.ServicesModule
              ),
          },
          {
            path: "prices",
            loadChildren: () =>
              import("./modules/prices/prices.module").then(
                (m) => m.PricesModule
              ),
          },
          {
            path: "subscriptions",
            loadChildren: () =>
              import("./modules/subscriptions/subscriptions.module").then(
                (m) => m.SubscriptionsModule
              ),
          },
          {
            path: "communications",
            loadChildren: () =>
              import("./modules/communications/communications.module").then(
                (m) => m.CommunicationsModule
              ),
          },
          {
            path: "balances",
            loadChildren: () =>
              import("./modules/balances/balances.module").then(
                (m) => m.BalancesModule
              ),
          },
          {
            path: "access-logs",
            loadChildren: () =>
              import("./modules/access-logs/access-logs.module").then(
                (m) => m.AccessLogsModule
              ),
          },
          {
            path: "deadlines",
            loadChildren: () =>
              import("./modules/deadlines/deadlines.module").then(
                (m) => m.DeadlinesModule
              ),
          },
          {
            path: "groundings",
            loadChildren: () =>
              import("./modules/groundings/groundings.module").then(
                (m) => m.GroundingsModule
              ),
          },
          {
            path: "caretakers",
            loadChildren: () =>
              import("./modules/caretakers/caretakers.module").then(
                (m) => m.CaretakersModule
              ),
          },
          {
            path: "summary-docs",
            loadChildren: () =>
              import("./modules/summary-docs/summary-docs.module").then(
                (m) => m.SummaryDocsModule
              ),
          },
          {
            path: "tank",
            loadChildren: () =>
              import("./modules/tank/tank.module").then((m) => m.TankModule),
          },
          {
            path: "historical-docs",
            loadChildren: () =>
              import("./modules/historical-docs/historical-docs.module").then((m) => m.HistoricalDocsModule),
          },
          {
            path: "search-docs",
            loadChildren: () =>
              import("./modules/search-docs/search-docs.module").then(
                (m) => m.SearchDocsModule
              ),
          },
          {
            path: "add-documents",
            loadChildren: () =>
              import("./modules/add-documents/add-documents.module").then(
                (m) => m.AddDocumentsModule
              ),
          },
          // Document insert on the 3.0.0 archive, for any property. Reachable by URL only until the
          // cut-over moves the sidebar entry here from "add-documents"
          {
            path: "add-documentsV3",
            loadChildren: () =>
              import("./modules/add-documents-v3/add-documents-v3.module").then(
                (m) => m.AddDocumentsV3Module
              ),
          },
          {
            path: "ddts",
            loadChildren: () =>
              import("./modules/ddts/ddts.module").then((m) => m.DdtsModule),
          },
          {
            path: "stats",
            loadChildren: () =>
              import("./modules/stats/stats.module").then((m) => m.StatsModule),
          },
          {
            path: "settings",
            loadChildren: () =>
              import("./modules/settings/settings.module").then(
                (m) => m.SettingsModule
              ),
          },
          {
            path: "interventions",
            loadChildren: () =>
              import("./modules/interventions/interventions.module").then(
                (m) => m.InterventionsModule
              ),
          },
          {
            path: "electric-meters",
            loadChildren: () =>
              import("./modules/electric-meters/electric-meters.module").then(
                (m) => m.ElectricMetersModule
              ),
          },
          {
            path: "gas-meters",
            loadChildren: () =>
              import("./modules/gas-meters/gas-meters.module").then(
                (m) => m.GasMetersModule
              ),
          },
          {
            path: "water-meters",
            loadChildren: () =>
              import("./modules/water-meters/water-meters.module").then(
                (m) => m.WaterMetersModule
              ),
          },
          {
            path: "personal-data",
            loadChildren: () =>
              import("./modules/personal-data/personal-data.module").then(
                (m) => m.PersonalDataModule
              ),
          },
          {
            path: "generic-docs",
            loadChildren: () =>
              import("./modules/generic-docs/generic-docs.module").then(
                (m) =>m.GenericDocsModule
              ),
          },
          {
            path: "deleted-docs",
            loadChildren: () =>
              import("./modules/deleted-docs/deleted-docs.module").then(
                (m) => m.DeletedDocsModule
              ),
          },
          {
            path: "summary-docs-v3",
            loadChildren: () =>
              import("./modules/summary-docs-v3/summary-docs-v3.module").then(
                (m) => m.SummaryDocsV3Module
              ),
          },
          {
            path: "deleted-docs-v3",
            loadChildren: () =>
              import("./modules/deleted-docs-v3/deleted-docs-v3.module").then(
                (m) => m.DeletedDocsV3Module
              ),
          },
          {
            path: "deadlines-v3",
            loadChildren: () =>
              import("./modules/deadlines-v3/deadlines-v3.module").then(
                (m) => m.DeadlinesV3Module
              ),
          },
          {
            path: "historical-docs-v3",
            loadChildren: () =>
              import("./modules/historical-docs-v3/historical-docs-v3.module").then(
                (m) => m.HistoricalDocsV3Module
              ),
          },
          // Arturo's pages: in the sidebar only with the arturoLive switch (@see arturo-switch.utils)
          {
            path: "arturo",
            loadChildren: () =>
              import("./modules/arturo/arturo.module").then((m) => m.ArturoModule),
          },
          {
            path: "**",
            redirectTo: "it/errors/404",
          },
        ],
      },
      {
        path: "errors",
        loadChildren: () =>
          import("./modules/errors/errors.module").then((m) => m.ErrorsModule),
      },
      { path: "", pathMatch: "full", redirectTo: "auth/login" },
    ],
  },
  { path: "", redirectTo: "it", pathMatch: "full" },
  { path: "**", redirectTo: "it/errors/404" },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      // scrollPositionRestoration: 'top',
      anchorScrolling: "enabled",
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
